import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from '../../../../config/axios';
import { InputDiv, Label, Input, SearchOptions, Option } from '../../../../components/styles/select-style';

interface ItemType {
  id: number;
}

interface CompanySelectProps<T extends ItemType> {
  label: string;
  apiUrl: string;
  displayField: keyof T;
  queryParams?: Record<string, string | number>; 
  value: T;
  onChange: (item: T) => void;
}

const CompanySelect = <T extends ItemType>({
  label,
  apiUrl,
  displayField,
  queryParams = {}, 
  value,
  onChange,
}: CompanySelectProps<T>) => {
  const [items, setItems] = useState<T[]>([]);
  const [allCompanies, setAllCompanies] = useState<T[]>([]);
  const [inputItem, setInputItem] = useState<string>(value[displayField] as string || '');
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setInputItem(value[displayField] as string || '');
  }, [value.id, displayField]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get<T[]>(apiUrl, {
          params: { ...queryParams, loadQuantity: 1000 },
        });

        if (Array.isArray(response.data)) {
          const companies = response.data;
          setAllCompanies(companies);
          setItems(companies);   
        } else {
          console.error("Resposta inválida da API, esperado um array.");
          setAllCompanies([]);
          setItems([]);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setAllCompanies([]);
        setItems([]);
      }
    };
    fetchCompanies();
  }, [apiUrl, JSON.stringify(queryParams)]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);
    
    const filterCriteria = newValue.toLowerCase();
    
    if (newValue === '') {
      setItems(allCompanies);
    } else {
      const filteredItems = allCompanies.filter(item =>
        (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria)
      );
      setItems(filteredItems); 
    }
    
    setSelectedIndex(0); 

    onChange({ id: 0, [displayField]: newValue } as unknown as T);
    setShowOptions(true);
  };

  const handleSelectItem = (item: T) => {
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && selectedIndex < items.length - 1) {
      setSelectedIndex(prev => prev + 1);
      e.preventDefault();
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      if (items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      }
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      setShowOptions(false);
    }
  };

  return (
    <InputDiv>
      <Label>{label}</Label>
      <Input
        ref={inputRef}
        type='text'
        placeholder='Selecione'
        value={inputItem}
        onFocus={() => setShowOptions(true)}
        onChange={handleChangeInput}
        onKeyDown={handleSetOptionByKey}
      />
      {showOptions && items && items.length > 0 &&
        <SearchOptions ref={optionsRef}>
          {items.map((s, index) =>
            <Option
              key={s.id}
              onClick={() => handleSelectItem(s)}
              isSelected={index === selectedIndex}
            >
              {s[displayField] as React.ReactNode}
            </Option>
          )}
        </SearchOptions>
      }
    </InputDiv>
  );
};

export default CompanySelect;

